import { useState, useEffect } from 'react';
import { FileText, Save, RefreshCw, Plus, Trash2, Edit3, CheckCircle, AlertCircle, Globe, Server } from 'lucide-react';

interface ZoneInfo {
  name: string;
  type: string;
  zone_type: 'forward' | 'reverse';
  file_path: string;
  full_path: string;
  file_exists: boolean;
  file_size: number;
  record_count: number;
  modified: string;
}

interface ZoneFile {
  filename: string;
  filepath: string;
  content: string;
  parsed: {
    ttl: number | null;
    origin: string | null;
    soa: {
      name: string;
      ttl: string;
      primary_ns: string;
      admin_email: string;
      serial?: string;
      refresh?: string;
      retry?: string;
      expire?: string;
      minimum_ttl?: string;
    } | null;
    records: {
      name: string;
      ttl: number;
      type: string;
      value: string;
      priority: number;
    }[];
  };
}

const ZoneEditorLive = () => {
  const [zones, setZones] = useState<ZoneInfo[]>([]);
  const [selectedZone, setSelectedZone] = useState<ZoneFile | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [activeTab, setActiveTab] = useState<'files' | 'editor'>('files');
  const [editMode, setEditMode] = useState<'visual' | 'raw'>('visual');
  const [rawContent, setRawContent] = useState('');

  // New record form
  const [newRecord, setNewRecord] = useState({
    name: '',
    type: 'A',
    value: '',
    ttl: 86400,
    priority: 0
  });

  useEffect(() => {
    scanZones();
  }, []);

  const scanZones = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/zonefiles?action=scan');
      const data = await response.json();
      
      if (data.success) {
        setZones(data.data.zones);
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to scan zones' });
    } finally {
      setLoading(false);
    }
  };

  const loadZoneFile = async (filename: string) => {
    setLoading(true);
    try {
      const response = await fetch(`/api/zonefiles?action=read&file=${encodeURIComponent(filename)}`);
      const data = await response.json();
      
      if (data.success) {
        setSelectedZone(data.data);
        setRawContent(data.data.content);
        setActiveTab('editor');
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to load zone file' });
    } finally {
      setLoading(false);
    }
  };

  const saveZoneFile = async (content: string, force: boolean = false) => {
    if (!selectedZone) return;
    
    setSaving(true);
    setMessage(null);
    
    try {
      const response = await fetch('/api/zonefiles?action=save', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          filename: selectedZone.filename,
          content: content,
          force: force
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setMessage({ type: 'success', text: 'Zone file saved successfully' });
        // Reload the zone file
        loadZoneFile(selectedZone.filename);
      } else {
        setMessage({ 
          type: 'error', 
          text: data.validation_errors ? data.validation_errors.join('\n') : data.error 
        });
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to save zone file' });
    } finally {
      setSaving(false);
    }
  };

  const reloadBind = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/zonefiles?action=reload', { method: 'POST' });
      const data = await response.json();
      
      if (data.success) {
        setMessage({ type: 'success', text: 'DNS Server reloaded successfully' });
      } else {
        setMessage({ type: 'error', text: data.error || 'Failed to reload DNS Server' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Network error' });
    } finally {
      setLoading(false);
    }
  };

  const addRecord = () => {
    if (!selectedZone) return;
    
    const record = {
      name: newRecord.name,
      ttl: newRecord.ttl,
      type: newRecord.type,
      value: newRecord.type === 'MX' || newRecord.type === 'SRV' 
        ? `${newRecord.priority} ${newRecord.value}` 
        : newRecord.value,
      priority: newRecord.priority
    };
    
    const updatedRecords = [...selectedZone.parsed.records, record];
    const newContent = generateZoneContent(selectedZone, updatedRecords);
    
    saveZoneFile(newContent);
    
    // Reset form
    setNewRecord({ name: '', type: 'A', value: '', ttl: 86400, priority: 0 });
  };

  const deleteRecord = (index: number) => {
    if (!selectedZone) return;
    
    if (!confirm('Are you sure you want to delete this record?')) return;
    
    const updatedRecords = selectedZone.parsed.records.filter((_, i) => i !== index);
    const newContent = generateZoneContent(selectedZone, updatedRecords);
    
    saveZoneFile(newContent);
  };

  const generateZoneContent = (zone: ZoneFile, records: any[]) => {
    const soa = zone.parsed.soa;
    const ttl = zone.parsed.ttl || 86400;
    
    let content = `;\n; Zone file for ${zone.parsed.origin || zone.filename.replace('.db', '')}\n; Generated by Netwall DNS Manager\n;\n\n`;
    content += `$TTL ${ttl}\n`;
    if (zone.parsed.origin) {
      content += `$ORIGIN ${zone.parsed.origin}.\n\n`;
    }
    
    // SOA Record
    if (soa) {
      const serial = soa.serial || date('Ymd') + '01';
      content += `@\tIN\tSOA\t${soa.primary_ns}.\t${soa.admin_email}. (\n`;
      content += `\t\t\t\t${serial}\t; Serial\n`;
      content += `\t\t\t\t${soa.refresh || 3600}\t; Refresh\n`;
      content += `\t\t\t\t${soa.retry || 1800}\t; Retry\n`;
      content += `\t\t\t\t${soa.expire || 604800}\t; Expire\n`;
      content += `\t\t\t\t${soa.minimum_ttl || 86400})\t; Minimum TTL\n\n`;
    }
    
    // NS Records
    content += `; Name Servers\n`;
    content += `@\tIN\tNS\t${soa?.primary_ns || 'ns1'}.\n`;
    content += `@\tIN\tNS\tns2.\n\n`;
    
    // Group records by type
    const recordsByType: { [key: string]: any[] } = {};
    records.forEach(record => {
      if (!recordsByType[record.type]) recordsByType[record.type] = [];
      recordsByType[record.type].push(record);
    });
    
    // Write records by type
    Object.entries(recordsByType).forEach(([type, typeRecords]) => {
      content += `; ${type} Records\n`;
      typeRecords.forEach(record => {
        let value = record.value;
        if (type === 'MX' || type === 'SRV') {
          value = record.priority + ' ' + value.replace(/^\d+\s*/, '');
        }
        content += `${record.name}\t${record.ttl}\tIN\t${type}\t${value}\n`;
      });
      content += `\n`;
    });
    
    return content;
  };

  const validateZone = async () => {
    if (!selectedZone) return;
    
    try {
      const response = await fetch(`/api/zonefiles?action=validate&file=${encodeURIComponent(selectedZone.filename)}`);
      const data = await response.json();
      
      setMessage({ 
        type: data.valid ? 'success' : 'error', 
        text: data.valid ? 'Zone file is valid' : data.output?.join('\n') || 'Validation failed'
      });
    } catch (err) {
      setMessage({ type: 'error', text: 'Validation failed' });
    }
  };

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-3xl font-bold">Live Zone File Editor</h1>
        <div className="flex gap-4">
          <button
            onClick={scanZones}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-400"
          >
            <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
            Scan Zones
          </button>
          <button
            onClick={reloadBind}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:bg-gray-400"
          >
            <Server size={18} />
            Reload DNS Server
          </button>
        </div>
      </div>

      {message && (
        <div className={`p-4 rounded mb-6 ${message.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
          <div className="flex items-center gap-2">
            {message.type === 'success' ? <CheckCircle size={20} /> : <AlertCircle size={20} />}
            <pre className="whitespace-pre-wrap">{message.text}</pre>
          </div>
        </div>
      )}

      <div className="flex gap-4 mb-6 border-b">
        <button
          onClick={() => setActiveTab('files')}
          className={`px-4 py-3 border-b-2 ${activeTab === 'files' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-600'}`}
        >
          Zone Files
        </button>
        <button
          onClick={() => setActiveTab('editor')}
          disabled={!selectedZone}
          className={`px-4 py-3 border-b-2 ${activeTab === 'editor' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-600'} disabled:text-gray-400`}
        >
          Editor {selectedZone && `- ${selectedZone.filename}`}
        </button>
      </div>

      {activeTab === 'files' && (
        <div className="bg-white rounded-lg shadow">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-sm font-medium text-gray-500">Zone Name</th>
                <th className="px-6 py-3 text-left text-sm font-medium text-gray-500">Type</th>
                <th className="px-6 py-3 text-left text-sm font-medium text-gray-500">File</th>
                <th className="px-6 py-3 text-left text-sm font-medium text-gray-500">Records</th>
                <th className="px-6 py-3 text-left text-sm font-medium text-gray-500">Modified</th>
                <th className="px-6 py-3 text-right text-sm font-medium text-gray-500">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {zones.map((zone) => (
                <tr key={zone.name} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <Globe size={18} className={zone.zone_type === 'reverse' ? 'text-purple-500' : 'text-blue-500'} />
                      <span className="font-medium">{zone.name}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 rounded text-xs ${zone.zone_type === 'reverse' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'}`}>
                      {zone.zone_type}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">
                    {zone.file_exists ? (
                      <span className="text-green-600">{zone.file_path}</span>
                    ) : (
                      <span className="text-red-600">Missing</span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm">{zone.record_count}</td>
                  <td className="px-6 py-4 text-sm text-gray-600">{zone.modified || '-'}</td>
                  <td className="px-6 py-4 text-right">
                    {zone.file_exists && (
                      <button
                        onClick={() => loadZoneFile(basename(zone.file_path))}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                      >
                        <Edit3 size={18} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {zones.length === 0 && !loading && (
                <tr>
                  <td colSpan={6} className="px-6 py-8 text-center text-gray-500">
                    No zones found. Click "Scan Zones" to scan for zone files.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {activeTab === 'editor' && selectedZone && (
        <div className="grid grid-cols-3 gap-6">
          {/* Records List */}
          <div className="col-span-2">
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold">DNS Records</h2>
                <div className="flex gap-2">
                  <button
                    onClick={() => setEditMode('visual')}
                    className={`px-3 py-1 rounded text-sm ${editMode === 'visual' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100'}`}
                  >
                    Visual
                  </button>
                  <button
                    onClick={() => setEditMode('raw')}
                    className={`px-3 py-1 rounded text-sm ${editMode === 'raw' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100'}`}
                  >
                    Raw
                  </button>
                </div>
              </div>

              {editMode === 'visual' ? (
                <>
                  {/* Add Record Form */}
                  <div className="mb-6 p-4 bg-gray-50 rounded">
                    <h3 className="font-medium mb-3">Add New Record</h3>
                    <div className="grid grid-cols-6 gap-3">
                      <input
                        type="text"
                        placeholder="Name"
                        value={newRecord.name}
                        onChange={(e) => setNewRecord({ ...newRecord, name: e.target.value })}
                        className="px-3 py-2 border rounded text-sm"
                      />
                      <select
                        value={newRecord.type}
                        onChange={(e) => setNewRecord({ ...newRecord, type: e.target.value })}
                        className="px-3 py-2 border rounded text-sm"
                      >
                        {['A', 'AAAA', 'CNAME', 'MX', 'NS', 'PTR', 'TXT', 'SRV'].map(t => (
                          <option key={t} value={t}>{t}</option>
                        ))}
                      </select>
                      <input
                        type="text"
                        placeholder="Value"
                        value={newRecord.value}
                        onChange={(e) => setNewRecord({ ...newRecord, value: e.target.value })}
                        className="col-span-2 px-3 py-2 border rounded text-sm"
                      />
                      <input
                        type="number"
                        placeholder="TTL"
                        value={newRecord.ttl}
                        onChange={(e) => setNewRecord({ ...newRecord, ttl: parseInt(e.target.value) })}
                        className="px-3 py-2 border rounded text-sm"
                      />
                      <button
                        onClick={addRecord}
                        disabled={!newRecord.name || !newRecord.value}
                        className="flex items-center justify-center gap-1 px-3 py-2 bg-green-600 text-white rounded text-sm hover:bg-green-700 disabled:bg-gray-400"
                      >
                        <Plus size={16} />
                        Add
                      </button>
                    </div>
                    {(newRecord.type === 'MX' || newRecord.type === 'SRV') && (
                      <input
                        type="number"
                        placeholder="Priority"
                        value={newRecord.priority}
                        onChange={(e) => setNewRecord({ ...newRecord, priority: parseInt(e.target.value) })}
                        className="mt-2 px-3 py-2 border rounded text-sm w-32"
                      />
                    )}
                  </div>

                  {/* Records Table */}
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left text-sm font-medium">Name</th>
                        <th className="px-4 py-2 text-left text-sm font-medium">Type</th>
                        <th className="px-4 py-2 text-left text-sm font-medium">Value</th>
                        <th className="px-4 py-2 text-left text-sm font-medium">TTL</th>
                        <th className="px-4 py-2 text-right text-sm font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {selectedZone.parsed.records.map((record, index) => (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="px-4 py-3 font-mono text-sm">{record.name}</td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-1 rounded text-xs ${
                              record.type === 'A' ? 'bg-blue-100 text-blue-800' :
                              record.type === 'AAAA' ? 'bg-green-100 text-green-800' :
                              record.type === 'CNAME' ? 'bg-yellow-100 text-yellow-800' :
                              record.type === 'MX' ? 'bg-purple-100 text-purple-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {record.type}
                            </span>
                          </td>
                          <td className="px-4 py-3 font-mono text-sm">{record.value}</td>
                          <td className="px-4 py-3 text-sm">{record.ttl}</td>
                          <td className="px-4 py-3 text-right">
                            <button
                              onClick={() => deleteRecord(index)}
                              className="p-1 text-red-600 hover:bg-red-50 rounded"
                            >
                              <Trash2 size={16} />
                            </button>
                          </td>
                        </tr>
                      ))}
                      {selectedZone.parsed.records.length === 0 && (
                        <tr>
                          <td colSpan={5} className="px-4 py-8 text-center text-gray-500">
                            No records found
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </>
              ) : (
                <div>
                  <textarea
                    value={rawContent}
                    onChange={(e) => setRawContent(e.target.value)}
                    className="w-full h-96 font-mono text-sm p-4 border rounded"
                  />
                  <div className="flex gap-2 mt-4">
                    <button
                      onClick={() => saveZoneFile(rawContent)}
                      disabled={saving}
                      className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-400"
                    >
                      {saving ? 'Saving...' : 'Save Changes'}
                    </button>
                    <button
                      onClick={() => saveZoneFile(rawContent, true)}
                      disabled={saving}
                      className="px-4 py-2 bg-yellow-600 text-white rounded hover:bg-yellow-700 disabled:bg-gray-400"
                    >
                      Force Save
                    </button>
                    <button
                      onClick={validateZone}
                      className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
                    >
                      Validate
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Zone Info Sidebar */}
          <div className="col-span-1">
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="font-semibold mb-4">Zone Information</h3>
              
              {selectedZone.parsed.soa && (
                <div className="space-y-3 mb-6">
                  <h4 className="text-sm font-medium text-gray-500">SOA Record</h4>
                  <div className="text-sm">
                    <p><span className="text-gray-500">Primary NS:</span> {selectedZone.parsed.soa.primary_ns}</p>
                    <p><span className="text-gray-500">Admin:</span> {selectedZone.parsed.soa.admin_email}</p>
                    <p><span className="text-gray-500">Serial:</span> {selectedZone.parsed.soa.serial}</p>
                    <p><span className="text-gray-500">Refresh:</span> {selectedZone.parsed.soa.refresh}</p>
                    <p><span className="text-gray-500">Retry:</span> {selectedZone.parsed.soa.retry}</p>
                    <p><span className="text-gray-500">Expire:</span> {selectedZone.parsed.soa.expire}</p>
                  </div>
                </div>
              )}
              
              <div className="space-y-2">
                <h4 className="text-sm font-medium text-gray-500">File Details</h4>
                <p className="text-sm"><span className="text-gray-500">Path:</span> {selectedZone.filepath}</p>
                <p className="text-sm"><span className="text-gray-500">Default TTL:</span> {selectedZone.parsed.ttl || 86400}</p>
                <p className="text-sm"><span className="text-gray-500">Origin:</span> {selectedZone.parsed.origin || '@'}</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

function basename(path: string): string {
  return path.split('/').pop() || path;
}

function date(format: string): string {
  const d = new Date();
  if (format === 'Ymd') {
    return d.getFullYear().toString() + 
           String(d.getMonth() + 1).padStart(2, '0') + 
           String(d.getDate()).padStart(2, '0');
  }
  return '';
}

export default ZoneEditorLive;
